import { useLocalStorage } from '@vueuse/core'
import { defineStore } from 'pinia'
import { computed } from 'vue'

// ============================================================
// MCP（Model Context Protocol）服务器配置 Store
// 全局共用：所有 AI 联系人共享同一套 MCP 列表
// 仅支持远程 HTTP/SSE，通过服务端中转解决 CORS 问题
// ============================================================

/** MCP 工具定义（从服务器拉取后缓存到本地） */
export interface MCPToolDef {
  name: string
  description: string
  inputSchema: Record<string, unknown>
}

/** MCP 服务器配置 */
export interface MCPServerConfig {
  /** 本地唯一标识 */
  id: string
  /** 显示名称，如「瑞幸点单」 */
  name: string
  /** 用途描述（可选） */
  description?: string
  /** HTTPS MCP 端点地址 */
  url: string
  /** Bearer token（可选，不出现在日志中） */
  apiKey?: string
  /** 附加请求头（可选） */
  headers?: Record<string, string>
  /** 是否启用 */
  enabled: boolean
  /** 添加时间戳 */
  addedAt: number
  /** 本地缓存的工具列表（手动同步后更新） */
  cachedTools?: MCPToolDef[]
  /** 上次同步时间戳 */
  lastSyncAt?: number
}

export const useMCPStore = defineStore('mcp', () => {
  // 持久化到 localStorage
  const servers = useLocalStorage<MCPServerConfig[]>('settings/mcp/servers', [])

  // ── 计算属性 ──────────────────────────────────────────────

  /** 已启用的服务器列表 */
  const enabledServers = computed(() =>
    servers.value.filter(s => s.enabled),
  )

  /** 已启用且有缓存工具的数量 */
  const enabledCount = computed(() => enabledServers.value.length)

  // ── 操作 ──────────────────────────────────────────────────

  /** 生成唯一 ID */
  function generateId(): string {
    return `mcp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
  }

  /** 添加服务器 */
  function addServer(config: Omit<MCPServerConfig, 'id' | 'addedAt'>): MCPServerConfig {
    const server: MCPServerConfig = {
      ...config,
      id: generateId(),
      addedAt: Date.now(),
    }
    servers.value = [...servers.value, server]
    return server
  }

  /** 更新服务器配置 */
  function updateServer(id: string, patch: Partial<Omit<MCPServerConfig, 'id' | 'addedAt'>>): void {
    servers.value = servers.value.map(s =>
      s.id === id ? { ...s, ...patch } : s,
    )
  }

  /** 删除服务器 */
  function removeServer(id: string): void {
    servers.value = servers.value.filter(s => s.id !== id)
  }

  /** 切换启用/禁用 */
  function toggleServer(id: string): void {
    servers.value = servers.value.map(s =>
      s.id === id ? { ...s, enabled: !s.enabled } : s,
    )
  }

  /** 更新服务器的缓存工具列表（同步后调用） */
  function updateCachedTools(id: string, tools: MCPToolDef[]): void {
    servers.value = servers.value.map(s =>
      s.id === id
        ? { ...s, cachedTools: tools, lastSyncAt: Date.now() }
        : s,
    )
  }

  /** 根据 ID 查找服务器 */
  function getServerById(id: string): MCPServerConfig | undefined {
    return servers.value.find(s => s.id === id)
  }

  return {
    servers,
    enabledServers,
    enabledCount,
    addServer,
    updateServer,
    removeServer,
    toggleServer,
    updateCachedTools,
    getServerById,
  }
})
